import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { Fragment, useImperativeHandle, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { twMerge } from "tailwind-merge";

/** Headless UI anchor placement: a side, optionally with start/end alignment. */
type DropdownAnchor =
    | "top" | "right" | "bottom" | "left"
    | "top start" | "top end"
    | "right start" | "right end"
    | "bottom start" | "bottom end"
    | "left start" | "left end";

interface DropdownProps {
    children: any;
    id?: string;
    text?: React.ReactNode;
    buttonOverride?: any;
    buttonOverrideClassName?: string;
    disabled?: boolean;
    attachToSide?: "left" | "right" | "top";
    className?: string;
    /** The dropdown's outermost element. Needed to add min-w-0 where the dropdown is a flex item. */
    wrapperClassName?: string;
    itemWrapperClassName?: string;
    flat?: boolean;
    dividedItems?: boolean;
    childClassName?: string;
    style?: React.CSSProperties;
    ref?: React.Ref<{ close: () => void }>;
    onClose?: () => void;
    onOpen?: () => void;
    usePortal?: boolean;
    /** Headless UI anchor placement for the portalled panel. Only applies when
     *  usePortal is set. Defaults to "bottom end" (drops below, right-aligned).
     */
    portalAnchor?: DropdownAnchor;
}

/**
 *
 * @param text
 * @param children
 * @param buttonOverride
 * @param {"left"|"right"|"top"} attachToSide
 * @param {string} className
 * @param {boolean} flat
 * @param {string} itemWrapperClassName
 * @param {boolean} noDivider
 * @returns {Element}
 * @constructor
 */
const Dropdown: React.FC<DropdownProps> = ({
    text,
    children,
    buttonOverride,
    buttonOverrideClassName,
    disabled,
    attachToSide,
    className,
    wrapperClassName,
    itemWrapperClassName,
    flat,
    dividedItems = false,
    id,
    style,
    childClassName,
    ref,
    onClose,
    onOpen,
    usePortal = false,
    portalAnchor = "bottom end",
}) => {
    if (!Array.isArray(children)) children = [children];
    // filter out any nulls
    children = children.filter((child: any) => child);

    const buttonClasses = flat
        ? `${className} hover:bg-stone-100 rounded py-0.5 px-2`
        : `${className} py-[3px] px-2 border border-stone-200 shadow gap-1 rounded font-medium flex items-center justify-between bg-white hover:bg-stone-50 active:bg-stone-100`;

    let baseClasses = "absolute z-30 mt-1 border border-stone-200 rounded-2xl shadow bg-white focus:outline-none overflow-hidden";
    let dividedClasses = dividedItems ? "divide-y divide-gray-100" : "";

    /**
     * For top, we want to position it above the button
     * This is useful for certain dropdowns where you want to show the menu above the button.
     * Note: This will only work if the button is not at the top of the viewport.
     */
    let sideClasses = "";
    if (!usePortal) {
        if (attachToSide === "left") {
            sideClasses = "left-0";
        } else if (attachToSide === "right") {
            sideClasses = "right-0";
        } else if (attachToSide === "top") {
            sideClasses = "bottom-full mb-1";
        }
    }
    const itemClasses = twMerge(baseClasses, dividedClasses, sideClasses, itemWrapperClassName);

    const closeRef = useRef<(() => void) | null>(null);

    // Expose the close method to parent components.
    // This allows the parent to programmatically close the dropdown by calling ref.current.close()
    useImperativeHandle(
        ref,
        () => ({
            close: () => {
                closeRef.current?.();
            },
        }),
        [closeRef],
    );

    return (
        <Menu as="div" className={twMerge("relative", wrapperClassName)}>
            {({ close }) => {
                closeRef.current = close;
                return (
                    <>
                        <div className="flex items-center">
                            {/* if there's a button override, add no classes to this wrapper button */}
                            <MenuButton
                                disabled={disabled}
                                className={buttonOverride ? buttonOverrideClassName : buttonClasses}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                {buttonOverride || (
                                    <>
                                        {text}
                                        {flat ? "" : <ChevronDown className="h-4 w-4 stroke-stone-400" />}
                                    </>
                                )}
                            </MenuButton>
                        </div>

                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                            afterLeave={() => onClose?.()}
                            beforeEnter={() => onOpen?.()}
                        >
                            <MenuItems
                                anchor={usePortal ? portalAnchor : undefined}
                                className={itemClasses}
                                style={style}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                {children.map((child: any, i: number) => (
                                    <MenuItem key={child.props?.key || i}>
                                        <div className={childClassName}>{child}</div>
                                    </MenuItem>
                                ))}
                            </MenuItems>
                        </Transition>
                    </>
                );
            }}
        </Menu>
    );
};

export default Dropdown;
